/**
 * System Lizard - Main Application Entry (v1.2 Hardware Replica)
 * Clean preset loading and stable Web Audio patch cable management.
 */

import { SynthEngine } from './audio/synthEngine.js';
import { LFOEngine } from './audio/lfo.js';
import { AudioRecorder } from './audio/recorder.js';
import { RotaryKnob } from './ui/knob.js';
import { PatchBay } from './ui/patchBay.js';
import { ScopeVisualizer } from './ui/visualizer.js';
import { MIDIHandler } from './ui/midi.js';
import { PresetManager, FACTORY_PRESETS } from './ui/presets.js';

document.addEventListener('DOMContentLoaded', async () => {

  // 1. Initialize Audio Core
  const synth = new SynthEngine();
  const lfo = new LFOEngine();
  const recorder = new AudioRecorder(synth);

  // 2. Initialize Hardware Knobs (User Manual v1.2 Specifications)
  const knobConfigs = [
    { id: 'knob-coarse', param: 'coarseFreq', label: 'COARSE', min: 8, max: 8000, value: 220, unit: 'Hz', step: 1 },
    { id: 'knob-fm', param: 'fmAttenuvert', label: 'FM', min: -1.0, max: 1.0, value: 0.0, step: 0.01 },
    { id: 'knob-radius', param: 'radius', label: 'RADIUS', min: 0.1, max: 4.0, value: 1.5, unit: 'x', step: 0.01 },
    { id: 'knob-shape', param: 'shapeAttenuvert', label: 'SHAPE', min: 0.0, max: 1.0, value: 0.5, step: 0.01 },
    { id: 'knob-feedback', param: 'feedback', label: 'FEEDBACK', min: 0.0, max: 0.95, value: 0.45, step: 0.01 },
    { id: 'knob-freq', param: 'delayFreq', label: 'FREQ', min: 0.5, max: 50, value: 4.0, unit: 'Hz', step: 0.1 },
    { id: 'knob-mix', param: 'mix', label: 'MIX', min: 0.0, max: 1.0, value: 0.4, step: 0.01 },
    { id: 'knob-xfm', param: 'xfm', label: 'XFM', min: 0.0, max: 1.0, value: 0.2, step: 0.01 },
    { id: 'knob-xdt', param: 'xdt', label: 'XDT', min: 0.0, max: 1.0, value: 0.2, step: 0.01 },
    { id: 'knob-xam', param: 'xam', label: 'XAM', min: 0.0, max: 1.0, value: 0.3, step: 0.01 }
  ];

  const knobInstances = {};

  for (const cfg of knobConfigs) {
    const container = document.getElementById(cfg.id);
    if (container) {
      knobInstances[cfg.param] = new RotaryKnob(container, {
        ...cfg,
        onChange: (val) => {
          synth.setParam(cfg.param, val);
        }
      });
    }
  }

  // 3. Audio Power Button
  const btnPower = document.getElementById('btn-power');
  btnPower.addEventListener('click', async () => {
    await synth.ensureAudioContext();
    if (synth.ctx && !lfo.osc) {
      lfo.init(synth.ctx);
    }
    btnPower.textContent = '⚡ Audio Active';
    btnPower.style.background = '#e5b82a';
    btnPower.style.color = '#000000';
  });

  // 4. Scope Visualizer
  const canvas = document.getElementById('scope-canvas');
  const scope = new ScopeVisualizer(canvas, synth);
  scope.start();

  const scopeToggleBtn = document.getElementById('scope-mode-toggle');
  scopeToggleBtn.addEventListener('click', () => {
    if (scope.mode === 'oscilloscope') {
      scope.setMode('attractor');
      scopeToggleBtn.textContent = 'Switch to Oscilloscope';
    } else {
      scope.setMode('oscilloscope');
      scopeToggleBtn.textContent = 'Switch to XY Attractor';
    }
  });

  // 5. Patch Bay (9 Left Jacks + 2 Top Right Outputs)
  const svgCanvas = document.getElementById('patch-svg-canvas');
  const patchBay = new PatchBay(svgCanvas, {
    onConnect: (fromJack, toJack) => {
      synth.ensureAudioContext().then(() => {
        if (synth.ctx && !lfo.osc) {
          lfo.init(synth.ctx);
        }
        const cableId = `${fromJack.id}-${toJack.id}`;
        synth.connectPatch(cableId, fromJack.id, toJack.id, lfo);
      });
    },
    onDisconnect: (cable) => {
      synth.disconnectPatch(cable.id);
    }
  });

  // Register 9 Input Jacks + 2 Output Jacks
  const jacksList = [
    { id: 'jack-vo', type: 'in', name: '1V/Oct CV' },
    { id: 'jack-fm', type: 'in', name: 'FM CV' },
    { id: 'jack-rad', type: 'in', name: 'Radius CV' },
    { id: 'jack-shp', type: 'in', name: 'Shape CV' },
    { id: 'jack-fb', type: 'in', name: 'Feedback CV' },
    { id: 'jack-freq', type: 'in', name: 'Delay Freq CV' },
    { id: 'jack-xfm', type: 'in', name: 'XFM CV' },
    { id: 'jack-xdt', type: 'in', name: 'XDT CV' },
    { id: 'jack-xam', type: 'in', name: 'XAM CV' },
    { id: 'jack-mainout', type: 'out', name: 'Main Out' },
    { id: 'jack-auxout', type: 'out', name: 'Aux Out' }
  ];

  for (const j of jacksList) {
    const el = document.getElementById(j.id);
    if (el) {
      patchBay.registerJack(j.id, el, j.type, j.name);
    }
  }

  window.addEventListener('resize', () => patchBay.updateAllCables());

  // 6. Audio Recorder
  const btnRecord = document.getElementById('btn-record');
  btnRecord.addEventListener('click', async () => {
    await synth.ensureAudioContext();
    if (!recorder.isRecording) {
      const ok = recorder.startRecording();
      if (ok) {
        btnRecord.textContent = '⏹ Stop & Save';
        btnRecord.style.background = '#ff4757';
      }
    } else {
      const result = await recorder.stopRecording();
      btnRecord.textContent = '⏺ Record Sample';
      btnRecord.style.background = '';
      if (result && result.url) {
        const a = document.createElement('a');
        a.href = result.url;
        a.download = `system_lizard_iguanamente_${Date.now()}.webm`;
        a.click();
      }
    }
  });

  // 7. Preset Selector Manager (Clean Reset on Load)
  const presetSelector = document.getElementById('preset-selector');
  const presetMgr = new PresetManager(synth, (loadedPreset) => {
    // 1. Clear SVG cables from UI
    patchBay.clearAllCables();

    // 2. Clear Web Audio patch nodes & CV inputs
    synth.clearAllPatches();

    // 3. Update knob instances
    for (const [param, val] of Object.entries(loadedPreset.params)) {
      if (knobInstances[param]) {
        knobInstances[param].setValue(val);
      }
    }
  });

  FACTORY_PRESETS.forEach((preset, index) => {
    const opt = document.createElement('option');
    opt.value = index;
    opt.textContent = preset.name;
    presetSelector.appendChild(opt);
  });

  presetSelector.addEventListener('change', (e) => {
    const idx = parseInt(e.target.value, 10);
    if (!isNaN(idx) && FACTORY_PRESETS[idx]) {
      presetMgr.loadPreset(FACTORY_PRESETS[idx]);
    }
  });

  // 8. Audition Piano Keyboard UI
  const keyboardContainer = document.getElementById('piano-keyboard');
  const baseMidi = 60;
  const keyMap = ['a', 'w', 's', 'e', 'd', 'f', 't', 'g', 'y', 'h', 'u', 'j', 'k'];
  const notesStr = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B', 'C2'];

  let leftPos = 0;
  const whiteKeyWidthPercent = 100 / 8;

  notesStr.forEach((noteName, idx) => {
    const midiNote = baseMidi + idx;
    const isBlack = noteName.includes('#');
    const keyEl = document.createElement('div');
    keyEl.className = `key ${isBlack ? 'black' : 'white'}`;
    keyEl.dataset.midi = midiNote;

    if (isBlack) {
      keyEl.style.left = `${leftPos - 3.25}%`;
    } else {
      if (idx > 0) leftPos += whiteKeyWidthPercent;
    }

    const triggerNoteOn = () => {
      synth.noteOn(midiNote, 0.85);
      keyEl.classList.add('active');
    };

    const triggerNoteOff = () => {
      synth.noteOff(midiNote);
      keyEl.classList.remove('active');
    };

    keyEl.addEventListener('pointerdown', triggerNoteOn);
    keyEl.addEventListener('pointerup', triggerNoteOff);
    keyEl.addEventListener('pointermouseleave', triggerNoteOff);

    keyboardContainer.appendChild(keyEl);
  });

  // QWERTY Key listeners
  window.addEventListener('keydown', (e) => {
    if (e.repeat) return;
    const keyIndex = keyMap.indexOf(e.key.toLowerCase());
    if (keyIndex !== -1) {
      const midiNote = baseMidi + keyIndex;
      synth.noteOn(midiNote, 0.85);
      const keyEl = keyboardContainer.querySelector(`[data-midi="${midiNote}"]`);
      if (keyEl) keyEl.classList.add('active');
    }
  });

  window.addEventListener('keyup', (e) => {
    const keyIndex = keyMap.indexOf(e.key.toLowerCase());
    if (keyIndex !== -1) {
      const midiNote = baseMidi + keyIndex;
      synth.noteOff(midiNote);
      const keyEl = keyboardContainer.querySelector(`[data-midi="${midiNote}"]`);
      if (keyEl) keyEl.classList.remove('active');
    }
  });

  // 9. Web MIDI Setup
  const midi = new MIDIHandler(synth);
  midi.initMIDI();

});
